import { useState } from "react";
import PageShell from "@/components/PageShell";
import { useLang } from "@/context/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Briefcase, Check } from "lucide-react";

export default function Careers() {
    const { t } = useLang();
    const [form, setForm] = useState({
        name: "", email: "", phone: "", position: "", message: "", consent: false,
    });
    const [cv, setCv] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.consent) {
            toast.error("Bitte Zustimmung erteilen.");
            return;
        }
        setSubmitting(true);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
            if (cv) fd.append("cv", cv);
            await api.post("/careers/apply", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(t.careers.form.success);
            setForm({ name: "", email: "", phone: "", position: "", message: "", consent: false });
            setCv(null);
            e.target.reset();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PageShell>
            <section className="relative bg-brand-green text-white overflow-hidden">
                <div className="absolute inset-0 grainy opacity-50" />
                <div className="container-x relative py-20 md:py-24">
                    <div className="eyebrow text-brand-yellow">{t.careers.eyebrow}</div>
                    <h1 className="mt-4 font-display font-extrabold text-4xl md:text-6xl lg:text-7xl text-balance max-w-3xl">{t.careers.title}</h1>
                    <p className="mt-5 text-white/80 max-w-2xl text-lg">{t.careers.body}</p>
                </div>
            </section>

            <section className="container-x py-20 grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-5">
                    <h2 className="font-display text-3xl font-bold">{t.careers.why}</h2>
                    <ul className="mt-5 space-y-3">
                        {t.careers.whyPoints.map((p, i) => (
                            <li key={i} className="flex items-start gap-3 rounded-xl border border-black/5 bg-white p-4">
                                <div className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-green text-brand-yellow"><Check className="h-3.5 w-3.5" /></div>
                                <span className="text-sm text-brand-charcoal/80">{p}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-10">
                        <h3 className="font-display text-xl font-semibold flex items-center gap-2"><Briefcase className="h-4 w-4 text-brand-green" />{t.careers.openTitle}</h3>
                        <div className="mt-4 flex flex-wrap gap-2">
                            {t.careers.positions.map((p) => (
                                <span key={p} className="rounded-full bg-brand-cream-dark/60 px-3 py-1.5 text-xs font-semibold text-brand-charcoal">{p}</span>
                            ))}
                        </div>
                    </div>
                </div>

                <form className="lg:col-span-7 card-clean p-7 md:p-9" onSubmit={submit} data-testid="careers-form">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label={t.careers.form.name}><input required value={form.name} onChange={onChange("name")} className="input-clean" data-testid="careers-name" /></Field>
                        <Field label={t.careers.form.email}><input required type="email" value={form.email} onChange={onChange("email")} className="input-clean" data-testid="careers-email" /></Field>
                        <Field label={t.careers.form.phone}><input value={form.phone} onChange={onChange("phone")} className="input-clean" data-testid="careers-phone" /></Field>
                        <Field label={t.careers.form.position}>
                            <select value={form.position} onChange={onChange("position")} className="input-clean" data-testid="careers-position">
                                <option value="">—</option>
                                {t.careers.positions.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </Field>
                    </div>
                    <Field label={t.careers.form.message}>
                        <textarea rows={4} value={form.message} onChange={onChange("message")} className="input-clean" data-testid="careers-message" />
                    </Field>
                    <Field label={t.careers.form.cv}>
                        <label className="flex items-center gap-3 rounded-xl border border-dashed border-black/15 bg-white px-4 py-3 cursor-pointer hover:border-brand-green/40">
                            <Upload className="h-4 w-4 text-brand-green" />
                            <span className="text-sm text-brand-charcoal/70">{cv ? cv.name : t.careers.form.cv}</span>
                            <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setCv(e.target.files?.[0] || null)} className="hidden" data-testid="careers-cv" />
                        </label>
                    </Field>
                    <label className="mt-2 flex items-start gap-3 text-xs text-brand-charcoal/70">
                        <input type="checkbox" checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} className="mt-0.5 h-4 w-4" data-testid="careers-consent" />
                        <span>{t.careers.form.consent}</span>
                    </label>
                    <button disabled={submitting} className="mt-6 btn-primary disabled:opacity-50" data-testid="careers-submit">
                        {submitting ? "..." : t.cta.apply}
                    </button>
                </form>
            </section>
        </PageShell>
    );
}

function Field({ label, children }) {
    return (
        <div className="mb-4 last:mb-0">
            <label className="block text-xs font-semibold uppercase tracking-widest text-brand-charcoal/60 mb-2">{label}</label>
            {children}
        </div>
    );
}
